import { type FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Trash2 } from "lucide-react";
import { CrmSelect, crmSelectFieldWrapperClass } from "@/components/crm/CrmListChevron";
import { CrmOwnerSelect } from "@/components/crm/CrmOwnerSelect";
import { LeadAccountFields, LeadContactFields, emptyChannelsState } from "@/components/crm/CrmLeadFormFields";
import { ViewportNotification } from "@/components/ui/viewportNotification";
import { apiGet } from "@/lib/apiClient";
import {
  addressOptionsFromLeadFields,
  defaultAddressCountryIdFromLeadFields,
} from "@/lib/crmContactAddresses";
import {
  channelOptionsFromLeadFields,
  defaultPhoneCountryIdFromLeadFields,
} from "@/lib/crmContactChannels";
import {
  CRM_LEAD_SOURCES,
  formatCrmLeadTypeLabel,
  formatCrmSourceLabel,
  formatCrmStatusLabel,
  normalizeCrmSourceForSave,
} from "@/lib/crmDisplay";
import {
  buildLeadCompanyDetailView,
  buildLeadContactChannelsView,
  type LeadDetailContactSnapshot,
  type LeadDetailFetchedContact,
  type LeadLinkedAccountSnapshot,
} from "@/lib/crmLeadDetailDisplay";
import { persistLeadEdit, createEmptyLeadEditContactRow, deriveLeadNameForSave, type LeadEditContactRow } from "@/lib/crmLeadEditPayload";
import { fetchCrmLeadModuleFields, type CrmLeadModuleFieldsDocument } from "@/lib/crmLeadModuleFieldsApi";
import { useCrmOrgUserDirectory } from "@/lib/crmOwnerUsers";
import {
  crmLeadFieldIsHidden,
  crmLeadIndustryValueFromLegacyText,
  crmLeadSectionFieldById,
  crmLeadSelectOptionsForForm,
  crmLeadSelectOptionsForParentForm,
  crmLeadStatusFieldById,
  standardIndustrySegmentSelectOptions,
  standardIndustrySelectOptions,
} from "@/lib/crmLeadModuleFields";

const LEAD_STAGES = [
  "new",
  "qualified",
  "meeting_booked",
  "proposal_sent",
  "negotiation",
  "closed_won",
  "closed_lost",
] as const;

const LEAD_RECORD_STATUSES = ["open", "working", "paused", "disqualified", "converted"] as const;

type LeadRecord = {
  id: string;
  leadType?: string;
  leadName: string;
  stage: string;
  score: number;
  status: string;
  ownerId: string | null;
  source: string;
  sourceDetail?: string | null;
  accountId?: string | null;
  companyName?: string | null;
  companyDomain?: string | null;
  companyWebsite?: string | null;
  companyIndustry?: string | null;
  contacts: LeadDetailContactSnapshot[];
};

function fallbackLeadStatusOptions(values: readonly string[]): { value: string; label: string }[] {
  return values.map((value) => ({ value, label: formatCrmStatusLabel(value) }));
}

export function CrmLeadEditPage() {
  const { leadId: idParam } = useParams<{ leadId: string }>();
  const leadId = idParam ? decodeURIComponent(idParam) : "";
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const detailHref = leadId
    ? `/crm/leads/${encodeURIComponent(leadId)}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`
    : "/crm/leads";

  const { users: orgUsers, loading: orgUsersLoading } = useCrmOrgUserDirectory();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [leadType, setLeadType] = useState("");
  const [accountId, setAccountId] = useState<string | null>(null);
  const [existingSourceDetail, setExistingSourceDetail] = useState<string | null>(null);
  const [showCompany, setShowCompany] = useState(false);

  const [existingLeadName, setExistingLeadName] = useState("");
  const [source, setSource] = useState("Manual");
  const [stage, setStage] = useState("new");
  const [status, setStatus] = useState("open");
  const [score, setScore] = useState("0");
  const [ownerId, setOwnerId] = useState("");

  const [companyName, setCompanyName] = useState("");
  const [companyDomain, setCompanyDomain] = useState("");
  const [website, setWebsite] = useState("");
  const [industry, setIndustry] = useState("");
  const [industrySegment, setIndustrySegment] = useState("");
  const [companyChannels, setCompanyChannels] = useState(() => emptyChannelsState("account"));

  const [contactRows, setContactRows] = useState<LeadEditContactRow[]>([]);

  const [leadFieldSections, setLeadFieldSections] = useState<
    CrmLeadModuleFieldsDocument["sections"] | null
  >(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState<{
    tone: "error" | "success";
    message: string;
  } | null>(null);

  const clearNotification = () => setNotification(null);
  const showError = (message: string) => setNotification({ tone: "error", message });

  useEffect(() => {
    let cancelled = false;
    void fetchCrmLeadModuleFields().then((doc) => {
      if (!cancelled) setLeadFieldSections(doc.sections);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setLoadError(null);
      const res = await apiGet<LeadRecord>(`/api/crm/leads/${encodeURIComponent(leadId)}`);
      if (cancelled) return;
      if (!res.ok || !res.data) {
        setLoadError(res.error || "Lead not found.");
        setLoading(false);
        return;
      }
      const lead = res.data;
      let linkedAccount: LeadLinkedAccountSnapshot | null = null;
      if (lead.accountId) {
        const accountRes = await apiGet<LeadLinkedAccountSnapshot>(
          `/api/crm/accounts/${encodeURIComponent(lead.accountId)}`,
        );
        if (accountRes.ok && accountRes.data) {
          linkedAccount = accountRes.data;
        }
      }

      const contactIds = (lead.contacts || []).map((c) => c.contactId).filter(Boolean) as string[];
      const fetchedById: Record<string, LeadDetailFetchedContact> = {};
      if (lead.accountId && contactIds.length) {
        const results = await Promise.all(
          contactIds.map((contactId) =>
            apiGet<LeadDetailFetchedContact>(
              `/api/crm/contacts/${encodeURIComponent(contactId)}?accountId=${encodeURIComponent(lead.accountId!)}`,
            ),
          ),
        );
        contactIds.forEach((id, index) => {
          const contactRes = results[index];
          if (contactRes?.ok && contactRes.data) {
            fetchedById[id] = { ...contactRes.data, id: contactRes.data.id || id };
          }
        });
      }

      if (cancelled) return;

      const companyView = buildLeadCompanyDetailView({ lead, account: linkedAccount });
      setLeadType(lead.leadType || "");
      setAccountId(lead.accountId ?? null);
      setExistingSourceDetail(lead.sourceDetail ?? null);
      setShowCompany(Boolean(companyView));
      setExistingLeadName(lead.leadName || "");
      setStage(lead.stage || "new");
      setStatus(lead.status || "open");
      setScore(String(lead.score ?? 0));
      setOwnerId(lead.ownerId || "");
      setSource(formatCrmSourceLabel(lead.source) || "Manual");

      if (companyView) {
        setCompanyName(companyView.name || "");
        setCompanyDomain(companyView.domain || "");
        setWebsite(companyView.website || "");
        setIndustry(companyView.industry || "");
        setIndustrySegment(companyView.industrySegment || "");
        setCompanyChannels(companyView.channels);
      } else {
        setCompanyName("");
        setCompanyDomain("");
        setWebsite("");
        setIndustry("");
        setIndustrySegment("");
        setCompanyChannels(emptyChannelsState("account"));
      }

      setContactRows(
        (lead.contacts || []).map((snapshot, index) => {
          const fetched =
            snapshot.contactId && fetchedById[snapshot.contactId]
              ? fetchedById[snapshot.contactId]
              : null;
          const channels = buildLeadContactChannelsView({
            snapshot,
            index,
            fetched,
            sourceDetail: lead.sourceDetail,
            leadType: lead.leadType,
          });
          return {
            rowKey: snapshot.contactId || crypto.randomUUID(),
            contactId: snapshot.contactId ?? null,
            firstName: snapshot.firstName?.trim() || fetched?.firstName?.trim() || "",
            lastName: snapshot.lastName?.trim() || fetched?.lastName?.trim() || "",
            title: snapshot.title?.trim() || fetched?.title?.trim() || "",
            channels,
          };
        }),
      );
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const accountChannelOptions = useMemo(
    () => channelOptionsFromLeadFields(leadFieldSections ?? undefined, "account_lead", "account"),
    [leadFieldSections],
  );
  const contactChannelOptions = useMemo(
    () => channelOptionsFromLeadFields(leadFieldSections ?? undefined, "contact_lead", "contact"),
    [leadFieldSections],
  );
  const accountDefaultPhoneCountryId = useMemo(
    () => defaultPhoneCountryIdFromLeadFields(leadFieldSections ?? undefined, "account_lead"),
    [leadFieldSections],
  );
  const contactDefaultPhoneCountryId = useMemo(
    () => defaultPhoneCountryIdFromLeadFields(leadFieldSections ?? undefined, "contact_lead"),
    [leadFieldSections],
  );
  const accountDefaultAddressCountryId = useMemo(
    () => defaultAddressCountryIdFromLeadFields(leadFieldSections ?? undefined, "account_lead"),
    [leadFieldSections],
  );
  const contactDefaultAddressCountryId = useMemo(
    () => defaultAddressCountryIdFromLeadFields(leadFieldSections ?? undefined, "contact_lead"),
    [leadFieldSections],
  );
  const accountAddressTypeOptions = useMemo(
    () => addressOptionsFromLeadFields(leadFieldSections ?? undefined, "account_lead"),
    [leadFieldSections],
  );
  const contactAddressTypeOptions = useMemo(
    () => addressOptionsFromLeadFields(leadFieldSections ?? undefined, "contact_lead"),
    [leadFieldSections],
  );

  const sourceField = useMemo(
    () => crmLeadStatusFieldById(leadFieldSections, "source"),
    [leadFieldSections],
  );
  const stageField = useMemo(
    () => crmLeadStatusFieldById(leadFieldSections, "stage"),
    [leadFieldSections],
  );
  const statusField = useMemo(
    () => crmLeadStatusFieldById(leadFieldSections, "status"),
    [leadFieldSections],
  );

  const sourceOptions = useMemo(() => {
    if (sourceField?.selectOptions?.length) {
      return crmLeadSelectOptionsForForm(sourceField.selectOptions, source);
    }
    return CRM_LEAD_SOURCES.map((s) => ({ value: s, label: s }));
  }, [sourceField, source]);

  const stageOptions = useMemo(() => {
    if (stageField?.selectOptions?.length) {
      return crmLeadSelectOptionsForForm(stageField.selectOptions, stage);
    }
    return fallbackLeadStatusOptions(LEAD_STAGES);
  }, [stageField, stage]);

  const statusOptions = useMemo(() => {
    if (statusField?.selectOptions?.length) {
      return crmLeadSelectOptionsForForm(statusField.selectOptions, status);
    }
    return fallbackLeadStatusOptions(LEAD_RECORD_STATUSES);
  }, [statusField, status]);

  const industryField = useMemo(
    () => crmLeadSectionFieldById(leadFieldSections, "account_lead", "industry"),
    [leadFieldSections],
  );
  const industrySegmentField = useMemo(
    () => crmLeadSectionFieldById(leadFieldSections, "account_lead", "industry_segment"),
    [leadFieldSections],
  );

  const industryOptions = useMemo(() => {
    if (industryField?.selectOptions?.length) {
      return crmLeadSelectOptionsForForm(
        industryField.selectOptions,
        crmLeadIndustryValueFromLegacyText(industry, industryField.selectOptions),
      );
    }
    return standardIndustrySelectOptions();
  }, [industryField, industry]);

  const industrySegmentOptions = useMemo(() => {
    const industryValue = crmLeadIndustryValueFromLegacyText(industry, industryOptions);
    return crmLeadSelectOptionsForParentForm(
      industrySegmentField?.selectOptions?.length
        ? industrySegmentField.selectOptions
        : standardIndustrySegmentSelectOptions(),
      industryValue,
      industrySegment,
    );
  }, [industrySegmentField, industry, industrySegment, industryOptions]);

  useEffect(() => {
    if (!industry.trim()) {
      if (industrySegment) setIndustrySegment("");
      return;
    }
    if (
      industrySegment &&
      industrySegmentOptions.length > 0 &&
      !industrySegmentOptions.some((o) => o.value === industrySegment)
    ) {
      setIndustrySegment("");
    }
  }, [industry, industrySegment, industrySegmentOptions]);

  const editPageTitle = useMemo(
    () =>
      deriveLeadNameForSave({
        showCompany,
        companyName,
        contacts: contactRows,
        existingLeadName,
      }),
    [showCompany, companyName, contactRows, existingLeadName],
  );

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    clearNotification();
    const scoreNum = Number(score);
    if (Number.isNaN(scoreNum)) {
      showError("Score must be a number.");
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await persistLeadEdit({
        leadId,
        accountId,
        leadType,
        existingLeadName,
        source: normalizeCrmSourceForSave(source),
        stage,
        status,
        score: scoreNum,
        ownerId,
        existingSourceDetail,
        showCompany,
        company: {
          name: companyName,
          domain: companyDomain,
          website,
          industry,
          industrySegment,
          channels: companyChannels,
        },
        contacts: contactRows,
        industryOptions,
      });
      if (!result.ok) {
        showError(result.error);
        return;
      }
      void navigate(detailHref);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!leadId) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        Missing lead id.
      </div>
    );
  }

  if (loading) {
    return <p className="text-sm text-slate-500">Loading lead for edit…</p>;
  }

  if (loadError) {
    return (
      <div className="space-y-3">
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {loadError}
        </div>
        <Link to="/crm/leads" className="text-sm font-medium text-indigo-600 hover:underline">
          Back to leads
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ViewportNotification
        message={notification?.message ?? null}
        tone={notification?.tone ?? "error"}
        onDismiss={clearNotification}
      />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <Link
            to={detailHref}
            className="mt-0.5 inline-flex shrink-0 rounded-md border border-gray-300 p-2 text-gray-700 hover:bg-gray-50 dark:border-white/20 dark:text-gray-200 dark:hover:bg-white/10"
            aria-label="Back to lead detail"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
          </Link>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Edit lead
            </p>
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">{editPageTitle}</h1>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link
            to={detailHref}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-white/20 dark:text-gray-200 dark:hover:bg-white/10"
          >
            Cancel
          </Link>
          <button
            type="submit"
            form="crm-lead-edit-form"
            disabled={isSubmitting}
            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
          >
            {isSubmitting ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      <form id="crm-lead-edit-form" onSubmit={(e) => void handleSubmit(e)} className="max-w-4xl space-y-6">
        <section className="rounded-lg border border-gray-200 p-4 dark:border-white/10">
          <h2 className="mb-3 text-sm font-semibold">Lead information</h2>
          <div className="space-y-3">
            <div className="grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <span className="text-gray-500">Type</span>
                <p className="font-medium">{formatCrmLeadTypeLabel(leadType)}</p>
              </div>
              <label className="text-sm">
                Score
                <input
                  type="number"
                  value={score}
                  onChange={(e) => setScore(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 dark:border-white/20 dark:bg-black/20"
                />
              </label>
            </div>
            <label className="block text-sm">
              Owner
              <CrmOwnerSelect
                value={ownerId}
                onChange={setOwnerId}
                users={orgUsers}
                loading={orgUsersLoading}
                disabled={isSubmitting}
              />
            </label>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {!sourceField || !crmLeadFieldIsHidden(sourceField) ? (
                <label className="text-sm">
                  {sourceField?.label ?? "Source"}
                  <CrmSelect
                    value={source}
                    onChange={(e) => setSource(e.target.value)}
                    wrapperClassName={`mt-1 ${crmSelectFieldWrapperClass}`}
                    className="bg-transparent py-2 pl-3"
                  >
                    {sourceOptions.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </CrmSelect>
                </label>
              ) : null}
              {!stageField || !crmLeadFieldIsHidden(stageField) ? (
                <label className="text-sm">
                  {stageField?.label ?? "Stage"}
                  <CrmSelect
                    value={stage}
                    onChange={(e) => setStage(e.target.value)}
                    wrapperClassName={`mt-1 ${crmSelectFieldWrapperClass}`}
                    className="bg-transparent py-2 pl-3"
                  >
                    {stageOptions.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </CrmSelect>
                </label>
              ) : null}
              {!statusField || !crmLeadFieldIsHidden(statusField) ? (
                <label className="text-sm">
                  {statusField?.label ?? "Status"}
                  <CrmSelect
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    wrapperClassName={`mt-1 ${crmSelectFieldWrapperClass}`}
                    className="bg-transparent py-2 pl-3"
                  >
                    {statusOptions.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </CrmSelect>
                </label>
              ) : null}
            </div>
          </div>
        </section>

        {showCompany ? (
          <LeadAccountFields
            companyName={companyName}
            setCompanyName={setCompanyName}
            companyDomain={companyDomain}
            setCompanyDomain={setCompanyDomain}
            website={website}
            setWebsite={setWebsite}
            industry={industry}
            setIndustry={setIndustry}
            industrySegment={industrySegment}
            setIndustrySegment={setIndustrySegment}
            industryOptions={industryOptions}
            industrySegmentOptions={industrySegmentOptions}
            channels={companyChannels}
            setChannels={setCompanyChannels}
            channelOptions={accountChannelOptions}
            defaultPhoneCountryId={accountDefaultPhoneCountryId}
            addressTypeOptions={accountAddressTypeOptions}
            defaultAddressCountryId={accountDefaultAddressCountryId}
            leadFieldSections={leadFieldSections}
            companyNameRequired
          />
        ) : null}

        <section className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-white/10">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold">Lead Contacts</h2>
            <button
              type="button"
              onClick={() => setContactRows((prev) => [...prev, createEmptyLeadEditContactRow()])}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-white/20 dark:text-gray-200 dark:hover:bg-white/10"
            >
              + Add Lead Contact
            </button>
          </div>
          {contactRows.length === 0 ? (
            <p className="text-sm text-gray-500">
              No contacts on this lead yet. Add a Lead Contact to capture people and channels.
            </p>
          ) : (
            contactRows.map((row, index) => (
              <div
                key={row.rowKey}
                className="space-y-3 rounded-md border border-gray-200 p-4 dark:border-white/10"
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                    {contactRows.length > 1 ? `Lead Contact ${index + 1}` : "Lead Contact"}
                  </p>
                  <button
                    type="button"
                    onClick={() =>
                      setContactRows((prev) => prev.filter((item) => item.rowKey !== row.rowKey))
                    }
                    className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-white/20 dark:text-gray-200 dark:hover:bg-white/10"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    Remove
                  </button>
                </div>
                {!row.contactId && !accountId ? (
                  <p className="text-xs text-amber-800 dark:text-amber-200">
                    This lead has no Lead Account yet. Contact details save on the lead record only until an account is linked.
                  </p>
                ) : null}
                <LeadContactFields
                  firstName={row.firstName}
                  setFirstName={(value) =>
                    setContactRows((prev) =>
                      prev.map((item) => (item.rowKey === row.rowKey ? { ...item, firstName: value } : item)),
                    )
                  }
                  lastName={row.lastName}
                  setLastName={(value) =>
                    setContactRows((prev) =>
                      prev.map((item) => (item.rowKey === row.rowKey ? { ...item, lastName: value } : item)),
                    )
                  }
                  title={row.title}
                  setTitle={(value) =>
                    setContactRows((prev) =>
                      prev.map((item) => (item.rowKey === row.rowKey ? { ...item, title: value } : item)),
                    )
                  }
                  channels={row.channels}
                  setChannels={(value) =>
                    setContactRows((prev) =>
                      prev.map((item) => (item.rowKey === row.rowKey ? { ...item, channels: value } : item)),
                    )
                  }
                  channelOptions={contactChannelOptions}
                  defaultPhoneCountryId={contactDefaultPhoneCountryId}
                  addressTypeOptions={contactAddressTypeOptions}
                  defaultAddressCountryId={contactDefaultAddressCountryId}
                  embedded
                />
              </div>
            ))
          )}
        </section>

        <div className="flex flex-wrap gap-2">
          <Link
            to={detailHref}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-white/20 dark:text-gray-200 dark:hover:bg-white/10"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isSubmitting}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-50"
          >
            {isSubmitting ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
